/**
 * <s-doc-reader> — Quiet reading aids for long product documentation.
 *
 * Builds a contents list from the h2 headings inside [data-doc-content],
 * highlights the section being read, drives a thin reading-progress bar,
 * and reveals a back-to-top control once the reader leaves the purchase area.
 *
 * Required child markup:
 *   [data-doc-content]          — Rich text whose h2 headings become sections.
 *   [data-doc-toc]              — One or more lists that receive contents links.
 *   template[data-doc-toc-item] — <li><a></a></li> markup cloned per heading (keeps Tailwind classes in Liquid).
 *   [data-doc-toc-wrap]         — Containers hidden when there are fewer than 2 headings.
 *
 * Commands (via commandfor/command on descendant buttons):
 *   --top — Scroll to the top of the page and move focus to the product title.
 *
 * State attributes set on the host (style with group-data-[...] variants):
 *   data-reading — The documentation region is on screen (shows the progress bar).
 *   data-top     — The page has scrolled past the first viewport (shows back to top).
 */
class SDocReader extends HTMLElement {
  #ac;
  #observer;
  #rafId = 0;
  #links = new Map();

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.#buildContents();

    window.addEventListener('scroll', () => this.#queueUpdate(), { passive: true, signal });
    window.addEventListener('resize', () => this.#queueUpdate(), { passive: true, signal });
    this.addEventListener('command', (event) => this.#onCommand(event), { signal });
    this.#queueUpdate();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#rafId);
    this.#links.clear();
  }

  #buildContents() {
    const content = this.querySelector('[data-doc-content]');
    const template = this.querySelector('template[data-doc-toc-item]');
    const lists = this.querySelectorAll('[data-doc-toc]');
    const headings = content ? [...content.querySelectorAll('h2')] : [];

    if (headings.length < 2 || !template) {
      this.querySelectorAll('[data-doc-toc-wrap]').forEach((wrap) => { wrap.hidden = true; });
      return;
    }

    const used = new Set();
    headings.forEach((heading, index) => {
      const base = (heading.textContent || '')
        .trim()
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, '-')
        .replace(/^-+|-+$/g, '') || `section-${index + 1}`;
      let id = heading.id || `detail-${base}`;
      while (used.has(id) || (!heading.id && document.getElementById(id))) id = `${id}-${index + 1}`;
      used.add(id);
      heading.id = id;
      heading.tabIndex = -1;
    });

    lists.forEach((list) => {
      list.replaceChildren();
      headings.forEach((heading) => {
        const item = template.content.firstElementChild.cloneNode(true);
        const link = item.querySelector('a');
        link.href = `#${heading.id}`;
        link.textContent = heading.textContent.trim();
        list.append(item);
        const group = this.#links.get(heading.id) || [];
        group.push(link);
        this.#links.set(heading.id, group);
      });
    });

    this.#observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting);
      if (!visible.length) return;
      const current = visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0].target.id;
      this.#links.forEach((group, id) => {
        group.forEach((link) => {
          if (id === current) link.setAttribute('aria-current', 'location');
          else link.removeAttribute('aria-current');
        });
      });
    }, { rootMargin: '0px 0px -65% 0px' });
    headings.forEach((heading) => this.#observer.observe(heading));
  }

  #queueUpdate() {
    cancelAnimationFrame(this.#rafId);
    this.#rafId = requestAnimationFrame(() => this.#update());
  }

  #update() {
    const rect = this.getBoundingClientRect();
    const viewport = window.innerHeight;
    const travel = Math.max(rect.height - viewport, 1);
    const progress = Math.min(Math.max(-rect.top / travel, 0), 1);
    this.style.setProperty('--doc-progress', progress.toFixed(4));
    this.toggleAttribute('data-reading', rect.top < viewport * 0.5 && rect.bottom > 0);
    this.toggleAttribute('data-top', window.scrollY > viewport);
  }

  #onCommand(event) {
    if (event.command !== '--top') return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    const title = document.querySelector('[data-product-title]');
    title?.focus({ preventScroll: true });
  }
}

if (!customElements.get('s-doc-reader')) {
  customElements.define('s-doc-reader', SDocReader);
}
